// src/lib/triage.js - Enhanced Auto-Triage Classification
//
// Improved heuristics to auto-classify Tier 1 / Tier 2 / Noise from text/tags/impact/priority.

// Critical service patterns that indicate production impact
const CRITICAL_SERVICES = [
  'checkout', 'payment', 'billing', 'auth', 'login', 'signup',
  'api gateway', 'load balancer', 'database', 'queue', 'cache'
];

// Business impact patterns
const HIGH_IMPACT_PATTERNS = [
  'revenue', 'customer', 'user', 'conversion', 'signup', 'purchase',
  'sla', 'compliance', 'security', 'data loss', 'corruption'
];

// Performance/reliability indicators
const RELIABILITY_PATTERNS = [
  'timeout', 'latency', 'slow', 'performance', 'memory leak',
  'disk space', 'cpu', 'throttling', 'rate limit'
];

// Infrastructure and system patterns
const INFRASTRUCTURE_PATTERNS = [
  'kubernetes', 'docker', 'aws', 'gcp', 'azure', 'deployment',
  'network', 'dns', 'ssl', 'certificate', 'firewall'
];

export function autoClassify(issue) {
  const text = `${issue.title || ""} ${issue.summary || ""} ${issue.description || ""} ${(issue.tags || []).join(" ")}`.toLowerCase();
  const tags = (issue.tags || []).map((t) => String(t).toLowerCase());

  // Get priority from JIRA (Highest, High, Medium, Low)
  const priority = (issue.priority || "").toLowerCase();
  const impact = Number(issue.impact || 0);

  // Environment classification
  const isProd = /\bprod(uction)?\b|live|main|prod-/.test(text) ||
                 tags.includes("production") || tags.includes("prod");
  const isStaging = /staging|sandbox|dev|test env|pre-prod/.test(text) ||
                   tags.includes("staging") || tags.includes("dev");

  // Issue type classification
  const isOutage = /outage|down|unavailable|not responding|503|502|504/.test(text);
  const is5xxError = /\b5\d{2}\b|5xx|server error|internal error/.test(text);
  const isCrash = /crash|fatal|exception|segfault|panic|abort|core dump/.test(text);
  const isSecurityIssue = /security|vulnerability|exploit|breach|unauthorized|injection/.test(text);
  const isDataIssue = /data loss|corruption|missing data|duplicate|inconsistent/.test(text);

  // Service impact classification
  const affectsCriticalService = CRITICAL_SERVICES.some(service =>
    text.includes(service) || tags.includes(service.replace(' ', '_')));
  const hasHighBusinessImpact = HIGH_IMPACT_PATTERNS.some(pattern => text.includes(pattern));
  const isReliabilityIssue = RELIABILITY_PATTERNS.some(pattern => text.includes(pattern));
  const isInfraIssue = INFRASTRUCTURE_PATTERNS.some(pattern => text.includes(pattern));

  // Performance indicators
  const isPerformance = /slow|latency|ttfb|timeout|regression|perf(ormance)?|load time/.test(text);
  const hasMetrics = /\d+ms|seconds?|minutes?|error rate|throughput|qps|rps/.test(text);

  // Noise indicators
  const isKnownNoise = /noisy alert|false positive|test alert|known issue|ignore/.test(text);
  const isThirdParty = /third.party|vendor|external|upstream/.test(text);

  // Calculate classification score
  let tier1Score = 0;
  let tier2Score = 0;
  let noiseScore = 0;

  // TIER 1 SCORING
  if (priority === "highest" || priority === "critical") tier1Score += 3;
  if (impact >= 5) tier1Score += 3;
  if (isProd && (isOutage || is5xxError)) tier1Score += 4;
  if (isProd && affectsCriticalService) tier1Score += 3;
  if (isSecurityIssue && isProd) tier1Score += 3;
  if (isDataIssue && isProd) tier1Score += 3;
  if (hasHighBusinessImpact && isProd) tier1Score += 2;
  if (text.includes("incident") || text.includes("emergency")) tier1Score += 2;

  // TIER 2 SCORING
  if (priority === "high") tier2Score += 2;
  if (impact >= 3) tier2Score += 2;
  if (isCrash && isProd) tier2Score += 3;
  if (isPerformance && isProd && hasMetrics) tier2Score += 2;
  if (isReliabilityIssue && isProd) tier2Score += 2;
  if (isInfraIssue) tier2Score += 1;
  if (affectsCriticalService && !isProd) tier2Score += 1;

  // NOISE SCORING
  if (isStaging || (!isProd && !isInfraIssue)) noiseScore += 2;
  if (priority === "low" || priority === "lowest") noiseScore += 2;
  if (impact <= 2) noiseScore += 2;
  if (isKnownNoise) noiseScore += 3;
  if (isThirdParty && !isOutage) noiseScore += 1;
  if (text.includes("monitoring") && !isOutage) noiseScore += 1;

  // Make classification decision
  if (tier1Score >= 4 && tier1Score > tier2Score && tier1Score > noiseScore) {
    return "tier1";
  }

  if (tier2Score >= 2 && tier2Score > noiseScore) {
    return "tier2";
  }

  if (noiseScore >= 3) {
    return "noise";
  }

  // Default fallbacks
  if (isProd && (isCrash || is5xxError || isOutage)) {
    return "tier1";
  }

  if (isProd || impact >= 3 || priority === "high") {
    return "tier2";
  }

  return "tier2"; // Conservative default
}

// Get classification explanation for debugging
export function getClassificationReason(issue) {
  const classification = autoClassify(issue);
  const text = `${issue.title || ""} ${issue.summary || ""} ${issue.description || ""}`.toLowerCase();
  const priority = (issue.priority || "").toLowerCase();
  const impact = Number(issue.impact || 0);

  const reasons = [];

  if (priority === "highest" || priority === "critical") {
    reasons.push("Critical priority");
  }

  if (impact >= 5) {
    reasons.push(`High impact (${impact})`);
  }

  if (/\bprod(uction)?\b/.test(text)) {
    reasons.push("Production environment");
  }

  if (/outage|down|5\d{2}/.test(text)) {
    reasons.push("Service outage indicators");
  }

  if (CRITICAL_SERVICES.some(service => text.includes(service))) {
    reasons.push("Affects critical service");
  }

  return {
    classification,
    reasons,
    confidence: reasons.length > 0 ? "high" : "medium"
  };
}
